<script setup>
import { ref, computed, reactive, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const submitting = ref(false);

const state = reactive({
  currentPassword: '',
  password: '',
  passwordConfirmation: '',
});

const rules = {
  currentPassword: { required },
  password: { minLength: minLength(6) },
  passwordConfirmation: {
    minLength: minLength(6),
    isEqPassword(value) {
      return value === state.password;
    },
  },
};

const v$ = useVuelidate(rules, state);

const inputStyles = {
  borderRadius: '0.5rem',
  padding: '0.375rem 0.75rem',
  fontSize: '0.875rem',
  marginBottom: '0.125rem',
};

const isDisabled = computed(
  () =>
    !state.currentPassword ||
    !state.passwordConfirmation ||
    !v$.value.passwordConfirmation.isEqPassword
);

const resetState = () => {
  state.currentPassword = '';
  state.password = '';
  state.passwordConfirmation = '';
  nextTick(() => v$.value.$reset());
};

const open = () => {
  resetState();
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

defineExpose({ open, close });

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('PROFILE_SETTINGS.FORM.ERROR'));
    return;
  }
  submitting.value = true;
  let message = t('PROFILE_SETTINGS.PASSWORD_UPDATE_SUCCESS');
  try {
    await store.dispatch('updatePassword', {
      password: state.password,
      passwordConfirmation: state.passwordConfirmation,
      currentPassword: state.currentPassword,
    });
    close();
  } catch (error) {
    message =
      parseAPIErrorResponse(error) || t('RESET_PASSWORD.API.ERROR_MESSAGE');
  } finally {
    submitting.value = false;
    useAlert(message);
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="md"
    :title="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.TITLE')"
    :description="$t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.NOTE')"
    :confirm-button-label="
      $t('PROFILE_SETTINGS.FORM.PASSWORD_SECTION.BTN_TEXT')
    "
    :disable-confirm-button="isDisabled"
    :is-loading="submitting"
    @confirm="handleSubmit"
    @close="resetState"
  >
    <div class="flex flex-col w-full gap-4">
      <woot-input
        v-model="state.currentPassword"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.currentPassword.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.PLACEHOLDER')"
        :error="
          v$.currentPassword.$error
            ? $t('PROFILE_SETTINGS.FORM.CURRENT_PASSWORD.ERROR')
            : ''
        "
        @input="v$.currentPassword.$touch"
        @blur="v$.currentPassword.$touch"
      />
      <woot-input
        v-model="state.password"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.password.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.PASSWORD.LABEL')"
        :placeholder="$t('PROFILE_SETTINGS.FORM.PASSWORD.PLACEHOLDER')"
        :error="
          v$.password.$error ? $t('PROFILE_SETTINGS.FORM.PASSWORD.ERROR') : ''
        "
        @input="v$.password.$touch"
        @blur="v$.password.$touch"
      />
      <woot-input
        v-model="state.passwordConfirmation"
        type="password"
        :styles="inputStyles"
        :class="{ error: v$.passwordConfirmation.$error }"
        :label="$t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.LABEL')"
        :placeholder="
          $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.PLACEHOLDER')
        "
        :error="
          v$.passwordConfirmation.$error
            ? $t('PROFILE_SETTINGS.FORM.PASSWORD_CONFIRMATION.ERROR')
            : ''
        "
        @input="v$.passwordConfirmation.$touch"
        @blur="v$.passwordConfirmation.$touch"
      />
    </div>
  </Dialog>
</template>
